/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useCurrentTime from '@/hooks/useCurrentTime';

const defaultEvents = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel'];
const oneMinute = 60e3;

export type InactivityContextType = {
  inactive: boolean;
  onActivity: () => void;
};

const InactivityContext = createContext<InactivityContextType | undefined>(undefined);

const getTimeDifferenceInSeconds = (timeout: number, currentTime: Date) => {
  const differenceInSeconds =
    timeout - currentTime.getTime() > 0 ? (timeout - currentTime.getTime()) / 1000 : 0;
  return Math.ceil(differenceInSeconds);
};

const useInactivityDectector = (
  ms: number = oneMinute,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enabled: boolean = true,
  // initialState: boolean = false,
  events: string[] = defaultEvents,
) => {
  const [isIdle, setIsIdle] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(Number.MIN_SAFE_INTEGER);
  const { getCurrentTime } = useCurrentTime();
  const timeoutReference = useRef<number>();
  // const lastActivityReference = useRef<Date>();

  const resetTimer = useCallback(() => {
    const currentTime = new Date(getCurrentTime());
    // lastActivityReference.current = currentTime;

    if (isIdle) {
      setIsIdle(false);
    }

    timeoutReference.current = new Date(currentTime).getTime() + ms;

    const newTimeRemaining = getTimeDifferenceInSeconds(timeoutReference.current, currentTime);
    setTimeRemaining(newTimeRemaining);
  }, [getCurrentTime, isIdle, ms]);

  const handleActivity = useCallback(() => {
    if (enabled) resetTimer();
  }, [resetTimer, enabled]);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && enabled) {
        const currentTime = new Date(getCurrentTime());
        const remainingTime = getTimeDifferenceInSeconds(
          timeoutReference.current || 0,
          currentTime,
        );
        if (remainingTime <= 0) {
          setIsIdle(true);
        } else {
          handleActivity();
        }
      }
    };

    if (enabled) {
      document.addEventListener('visibilitychange', handleVisibility);
    } else {
      document.removeEventListener('visibilitychange', handleVisibility);
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [handleActivity, getCurrentTime, enabled]);

  useEffect(() => {
    if (enabled) {
      for (const event of events) {
        window.addEventListener(event, handleActivity, { passive: true });
      }
      handleActivity();
    } else {
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
    }

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [events, handleActivity, enabled]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    const updateTimer = () => {
      const currentTime = new Date(getCurrentTime());
      const remainingTime = getTimeDifferenceInSeconds(timeoutReference.current || 0, currentTime);
      setTimeRemaining(remainingTime);

      if (remainingTime === 0 && !isIdle && timeoutReference.current) {
        setIsIdle(true);
      }
    };

    if (timeRemaining > 0 && enabled) {
      intervalId = setInterval(updateTimer, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timeRemaining, getCurrentTime, isIdle, enabled]);

  return { inactive: isIdle, onActivity: handleActivity };
};

export function InactivityProvider({
  children,
  ms,
  enabled,
}: {
  children: ReactNode;
  ms: number;
  enabled: boolean;
}) {
  const value = useInactivityDectector(ms, enabled);
  return <InactivityContext.Provider value={value}>{children}</InactivityContext.Provider>;
}

export const useInactivityResult = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('Inactivity result can be accessed within  InactivityProvider');
  }
  return context;
};
